import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckInResponseSchema, type Coords as ApiCoords } from '@web/shared';
import { apiRequest } from '@/lib/api';
import { useGeolocation, haversineM, requestCompassPermission, type Coords } from '@/lib/geo';
import { useNearbySpots, useOpenCircle } from '@/features/spots/api';
import {
  setCurrentSpot,
  useCurrentSpot,
  useEnsureCurrentSpotExists,
  spotLabel,
} from '@/features/spots/current';
import { useProfile } from '@/features/profile/api';
import { readGlowIntent, writeGlowIntent } from './glow-store';
import { usePresenceUpdate } from './api';
import { GlowContext, GlowLocationError, type GlowContextValue } from './glow-context-def';

/** A real, approved venue only counts as "close enough" to auto check-in into
 * within its own radius — beyond that we fall back to an ambient Spot instead
 * of guessing. */
function withinRadius(coords: Coords, spot: { lat: number; lng: number; radiusM: number }) {
  return haversineM(coords.lat, coords.lng, spot.lat, spot.lng) <= spot.radiusM;
}

/**
 * Owns Glow end-to-end for the whole signed-in app — mounted once by
 * `AppShell` so it keeps beating (and the header pill stays live) no matter
 * which `/app/*` tab is open, not just while the Radar page happens to be
 * mounted. The actual "am I visible" flag is a client intent persisted per
 * Spot (see `glow-store.ts`) — the server is asked to reconcile it on every
 * heartbeat, and only its `glowExpiresAt`/`inArea` answers are trusted back.
 */
export function GlowProvider({ children }: { children: ReactNode }) {
  useEnsureCurrentSpotExists();
  const current = useCurrentSpot();
  const spotId = current?.spot.id ?? null;
  const { data: profile } = useProfile();

  const [glowOn, setGlowOn] = useState(() => readGlowIntent(spotId));
  const [glowExpiresAt, setGlowExpiresAt] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [outOfArea, setOutOfArea] = useState(0);

  // Only run a live GPS watch when it's actually needed — you're glowing or
  // checked into a Spot (so the heartbeat/Radar need your position). Idle on
  // other tabs with Glow off, a `watchPosition` is pure battery drain; the
  // Glow-on tap falls back to a one-shot `requestOnce()` to cold-start.
  const geo = useGeolocation(glowOn || !!spotId);
  const nearby = useNearbySpots(spotId ? null : geo.coords);
  const openCircle = useOpenCircle();
  const presence = usePresenceUpdate();

  // Re-seed if the checked-in Spot changes under us (or clears).
  useEffect(() => {
    setGlowOn(readGlowIntent(spotId));
  }, [spotId]);

  /** Finds (or creates) somewhere to be checked into, silently. Prefers a real,
   * nearby, approved venue; falls back to the ambient Open Circle for your
   * ~600m cell — which is exactly what makes "anyone glowing nearby" work,
   * since everyone in that cell lands on the same Spot. */
  const resolveSpot = useCallback(
    async (coordsOverride?: Coords): Promise<string | null> => {
      if (spotId) return spotId;
      // A fix that just came back from `requestOnce()` won't be in `geo.coords`
      // yet (state update is async) — take it directly so the first Glow tap
      // after granting permission still lands.
      const fix = coordsOverride ?? geo.coords;
      if (!fix) return null;
      const coords: ApiCoords = {
        lat: fix.lat,
        lng: fix.lng,
        heading: fix.heading,
        accuracy: fix.accuracy,
      };
      setResolving(true);
      try {
        const real = (nearby.data?.spots ?? []).find(
          (s) => s.approved && !s.ephemeral && withinRadius(fix, s),
        );
        const checkIn = async (targetId: string) =>
          apiRequest(`/api/spots/${targetId}/check-in`, {
            method: 'POST',
            body: { coords },
            schema: CheckInResponseSchema,
          });

        let result;
        try {
          result = real ? await checkIn(real.id) : null;
        } catch {
          result = null; // GPS drift / stale listing — fall through to ambient.
        }
        if (!result) {
          const ambient = await openCircle.mutateAsync(fix);
          result = await checkIn(ambient.spot.id);
        }
        setCurrentSpot({ spot: result.spot, checkedInAt: Date.now() });
        return result.spot.id;
      } finally {
        setResolving(false);
      }
    },
    [spotId, geo.coords, nearby.data, openCircle],
  );

  const toggleGlow = useCallback(async () => {
    // This tap is a user gesture — the one chance to unlock the iOS compass so
    // "ahead / to your left" is real, not just a distance ring.
    void requestCompassPermission();
    if (glowOn) {
      writeGlowIntent(spotId, false);
      setGlowOn(false);
      return;
    }

    // Need a location fix to place you on the map. If we don't have one yet,
    // this tap is the right moment to ask — a passive `watchPosition` on mount
    // often can't surface the browser prompt.
    let fix = geo.coords;
    if (!fix) {
      if (geo.permission === 'denied') {
        throw new GlowLocationError(
          'Location is off for this site. Turn it back on in your browser settings, then tap Glow.',
        );
      }
      fix = await geo.requestOnce();
      if (!fix) {
        throw new GlowLocationError(
          'Nearmirer needs your location to put you on the map. Allow it, then tap Glow again.',
        );
      }
    }

    const id = await resolveSpot(fix);
    if (!id) {
      throw new GlowLocationError(
        "Couldn't place you on the map just now — try again in a moment.",
      );
    }
    writeGlowIntent(id, true);
    setGlowOn(true);
    // `geo` is a fresh object each render; the reactive bits are listed
    // explicitly and `requestOnce` is a stable callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glowOn, spotId, resolveSpot, geo.coords, geo.permission, geo.requestOnce]);

  // Heartbeat: push presence every ~10s whenever checked in — lives here
  // (not the Radar page) so Glow keeps beating while you're on any tab.
  // Pauses while the tab is hidden: a backgrounded tab shouldn't keep you on
  // everyone's Radar or keep pushing back the Glow auto-expiry — and skipping
  // those beats is the single biggest saving against the D1 read/write budget.
  useEffect(() => {
    if (!current || !geo.coords) return;
    const beat = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      presence.mutate({
        spotId: current.spot.id,
        coords: {
          lat: geo.coords!.lat,
          lng: geo.coords!.lng,
          heading: geo.coords!.heading,
          accuracy: geo.coords!.accuracy,
        },
        glowOn,
        glowMinutes: glowOn ? (profile?.profile.glowDefaultMinutes ?? 60) : undefined,
        appearanceBlurb: current.appearanceBlurb,
        section: current.section,
      });
    };
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      beat();
      timer = setInterval(beat, 10_000);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVisibility);
    if (!document.hidden) start();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.spot.id, geo.coords?.lat, geo.coords?.lng, glowOn]);

  // Keep the countdown in step with the server's expiry, and watch the
  // geofence: a few consecutive "not in area" heartbeats means we've actually
  // left, so drop the check-in back to "pick your Spot" everywhere at once.
  useEffect(() => {
    if (!presence.data) return;
    setGlowExpiresAt(presence.data.glowExpiresAt);
    if (presence.data.inArea) {
      setOutOfArea(0);
    } else {
      setOutOfArea((n) => {
        if (n + 1 >= 3) {
          setCurrentSpot(null);
          return 0;
        }
        return n + 1;
      });
    }
  }, [presence.data]);

  const value = useMemo<GlowContextValue>(
    () => ({
      glowOn,
      glowExpiresAt,
      resolving,
      outOfArea,
      spotName: current ? spotLabel(current.spot) : null,
      locationBlocked: geo.permission === 'denied',
      gpsAccuracy: geo.coords?.accuracy ?? null,
      toggleGlow,
    }),
    [
      glowOn,
      glowExpiresAt,
      resolving,
      outOfArea,
      current,
      geo.permission,
      geo.coords?.accuracy,
      toggleGlow,
    ],
  );

  return <GlowContext.Provider value={value}>{children}</GlowContext.Provider>;
}
